import { useState } from 'react';
import { PROVIDERS_DRAWER_ACTION_VALUES } from '../components/drawers';
import useTranslate from '../../../../../hooks/useTranslate';
import { useTranslation } from 'react-i18next';
import useTableMenu from '../../../../../hooks/useTableMenu';
import useGlobalFilters from '../../../../../hooks/useGlobalFilters';
import { useDeleteCarcinogenicProviderMutation, useEditCarcinogenicReportEstablishmentMutation } from '../../../../../services/carcinogenicApi';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/reduxHooks';
import useAlerts from './useAlerts';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../../constants/values';
import { handleErrors } from '../../../../../utils/common';

const useProvidersModule = () => {
    const dispatch = useAppDispatch();
    const [editCarcinogenicReport, { isLoading: isEditingCarcinogenicReportProvidersCheck }] =
        useEditCarcinogenicReportEstablishmentMutation();
        
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const selectedEstablishmentId = useAppSelector(
        (state) => state.establishment.selectedEstablishmentId
    );

    const establishmentData = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.establecimiento
    );

    const providersEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.proveedoresCheck
    );

     const radiologicalEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.radiologicosCheck
    );

    const toggleProvidersEmptyDeclarationCheck = ({ customValue }: { customValue?: boolean }) => {
        handleEditEmptyProvidersCheck();
    };

    const handleEditEmptyProvidersCheck = async () => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEstablishmentId) return;
            const response: any = await editCarcinogenicReport({
                reportId: selectedCarcinogenicReportId,
                body: {
                    idEstablecimiento: selectedEstablishmentId,
                    establecimiento: establishmentData,
                    proveedoresCheck: !providersEmptyDeclarationCheckState,
                    radiologicosCheck: radiologicalEmptyDeclarationCheckState
                }
            });
            if (response?.error) {
                if (response?.error?.data?.statusCode === 400) {
                    handleErrors(response?.error);
                    return;
                }
                enqueueSnackbar(t('enqueues.toggleCheck.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.toggleCheck.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo actualizar la declaración de proveedores',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const [selected, setSelected] = useState<any | null>(null);
    const [open, setOpen] = useState<string>('');
    const { t } = useTranslation('carcinogenicReport', { keyPrefix: 'modules.providersAndBuyers' });
    const { translate } = useTranslate(t);

    const { showConfirmDeleteAlert } = useAlerts({ translate });

    const [deleteProvider, { isLoading: isDeletingProvider }] =
        useDeleteCarcinogenicProviderMutation();

    const providersTableMenu = useTableMenu();
    const providersFilters = useGlobalFilters();

    const { anchorToEventElement, openMenu } = providersTableMenu;

    const handleClickNewProviderButton = () => {
        setOpen(PROVIDERS_DRAWER_ACTION_VALUES.NEW_PROVIDER);
    };

    const handleClickEditProviderButton = (selectedSubstance: any) => {
        setSelected(selectedSubstance);
        setOpen(PROVIDERS_DRAWER_ACTION_VALUES.EDIT_PROVIDER);
    };

    const handleClickDeleteProviderButton = (selectedSubstance: any) => {
        showConfirmDeleteAlert({
            confirmAction: () => handleDeleteProvider({ selectedSubstance })
        });
    };

    const handleClickAction = (event: any, item: any) => {
        setSelected(item);
        anchorToEventElement(event);
        openMenu();
    };

    const handleDeleteProvider = async ({ selectedSubstance }: any) => {
        try {
            if (
                !selectedCarcinogenicReportId ||
                !selectedSubstance?.id ||
                !selectedSubstance?.proveedor?.id
            )
                return;

            const response: any = await deleteProvider({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedSubstance?.id,
                providerId: selectedSubstance?.proveedor?.id
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.delete.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.delete.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo eliminar el proveedor',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return {
        selected,
        open,
        providersTableMenu,
        providersFilters,
        isDeletingProvider,
        providersEmptyDeclarationCheckState,
        isEditingCarcinogenicReportProvidersCheck,
        toggleProvidersEmptyDeclarationCheck,
        translate,
        setOpen,
        handleClickNewProviderButton,
        handleClickEditProviderButton,
        handleClickDeleteProviderButton,
        handleClickAction
    };
};

export default useProvidersModule;
